import { ChangeEvent, SyntheticEvent, useEffect, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useConditionalEffect, usePrevious } from 'react-delta';
import BootstrapForm from 'react-bootstrap/Form';
import BootstrapRow from 'react-bootstrap/Row';
import BootstrapCol from 'react-bootstrap/Col';
import pick from 'lodash/pick';
import cloneDeep from 'lodash/cloneDeep';

import * as K from 'common/constants';
import ELEMENT, { createTestId } from 'common/element';
import { AlertInfo, AlertInput } from 'common/graphqlTypes';

import Dialog from 'components/common/Dialog/Dialog';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import CheckboxRadio from 'components/common/CheckboxRadio/CheckboxRadio';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { accountDataState } from 'components/account/account.state';
import { alertsConfigState } from './alertsconfig.state';

import './SaveAlertDialog.scss';

export enum TriggerType {
  FREQUENCY = 'frequency',
  AVERAGE = 'average',
  TABLE = 'table',
}

type TriggerFormType = {
  frontText?: string;
  measureWords: Array<string>;
  functions?: Array<string>;
  endText: string;
};

// Constants
const TRIGGER_FORM_INFO: Record<string, TriggerFormType> = {
  [TriggerType.FREQUENCY]: {
    frontText: 'If it happens',
    measureWords: ['more', 'fewer'],
    endText: 'times in',
  },
  [TriggerType.AVERAGE]: {
    frontText: 'The average value is',
    measureWords: ['greater', 'less'],
    endText: 'for',
  },
  [TriggerType.TABLE]: {
    functions: ['tableRows', 'tableValue'],
    measureWords: ['greater', 'less'],
    endText: 'for',
  },
};

type TriggerOptionsType = {
  type: string;
  value: number;
  minutes: number;
  function?: string;
};

const DEFAULT_TRIGGER: TriggerOptionsType = {
  type: '',
  value: 0,
  minutes: 2,
};

export const DEFAULT_ALERT: AlertInput = {
  alertAddresses: '',
  description: '',
  trigger: '',
  gracePeriod: 0,
  renotifyPeriod: 60,
  resolutionDelay: 5,
};

const RESOLUTION_IMMEDIATE_ENABLED = 'immediate_resolution_enabled';
const RESOLUTION_DELAY_ENABLED = 'delay_resolution_enabled';
const RESOLUTION_DISABLED = 'resolution_disabled';

// types
type EventCallback = (ev: SyntheticEvent) => void;

type InputItemType = {
  label: string;
  as: 'textarea' | 'input';
  placeholder?: string;
  value?: string;
  onChangeHandler: (ev: SyntheticEvent) => void;
  testId?: ELEMENT;
};

type InputItemsType = Array<InputItemType>;

// props
type SaveAlertDialogProps = {
  title: string;
  alert?: AlertInput;
  show: boolean;
  close: () => void;
  showSaveAsNew?: boolean;
  triggerType?: TriggerType;
  handleSubmit: (alert: AlertInput) => void;
  handleSaveAsNewSubmit?: (alert: AlertInput) => void;
};

/**
 * This component allows the user to create or edit an alert.
 * If the alert is created from search/search graph/search trace,
 * it renders a friendly trigger input that allows the user to enter trigger parameters.
 *
 * @prop title                     the title of the dialog
 * @prop alert                     the alert to display in the dialog
 * @prop show                      if true, the dialog is displayed
 * @prop close                     function to call to close the dialog
 * @prop showSaveAsNew             if true, it shows 'Save as new' button in the dialog footer.
 * @prop triggerType               if present, it renders a specific type of trigger input.
 * @prop handleSubmit              the handler responsible for creating or editing an alert
 * @prop handleSaveAsNewSubmit     the handler responsible for creating a brand new alert from an existing one
 */

const SaveAlertDialog = ({
  title,
  alert = DEFAULT_ALERT,
  show,
  close,
  showSaveAsNew = false,
  triggerType,
  handleSubmit,
  handleSaveAsNewSubmit,
}: SaveAlertDialogProps): JSX.Element => {
  const [alertInfo, setAlertInfo] = useState(alert);
  const [triggers, setTriggers] = useState<TriggerOptionsType>(() => ({
    ...DEFAULT_TRIGGER,
    type: triggerType === TriggerType.AVERAGE ? 'greater' : 'more',
    function: triggerType === TriggerType.TABLE ? 'tableRows' : undefined,
  }));
  const [useGracePeriod, setUseGracePeriod] = useState(alertInfo.gracePeriod > 0);
  const [useRenotifyPeriod, setUseRenotifyPeriod] = useState(alertInfo.renotifyPeriod > 0);
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  const accountData = useRecoilValue(accountDataState);
  const alertsConfig = useRecoilValue(alertsConfigState);
  const [selectedResolutionOption, setSelectedResolutionOption] = useState(RESOLUTION_IMMEDIATE_ENABLED);

  // if alert prop changes, start new editing session
  const prevAlert = usePrevious(alert);
  useConditionalEffect(() => {
    setAlertInfo(cloneDeep(alert));
  }, prevAlert !== alert);

  useEffect(() => {
    if (alertInfo?.resolutionDelay === 9999) {
      setSelectedResolutionOption(RESOLUTION_DISABLED);
    } else if (alertInfo?.resolutionDelay > 0) {
      setSelectedResolutionOption(RESOLUTION_DELAY_ENABLED);
    }
  }, [alertInfo]);

  const defaultAlertPlaceholder = alertsConfig.defaultAlertAddress || accountData?.activeTeamV2?.emailAddress || '';

  /**
   * onchange handler for general inputs in this dialog.
   * This returns a curried function that sets AlertInfo states.
   *
   * @param field     The field to change.
   */
  const handleInputChange =
    (field: string): EventCallback =>
    (ev: SyntheticEvent): void => {
      const inputElement = ev.target as HTMLInputElement;
      const newAlertInfo = {
        ...alertInfo,
        [field]: inputElement.type === 'number' ? inputElement.valueAsNumber : inputElement.value,
      };
      setAlertInfo(newAlertInfo);
    };

  /**
   * onchange handler for trigger inputs.
   * This returns a curried function that sets Triggers states.
   *
   * @param field     The field to change.
   */
  const handleTriggerChange =
    (field: string): EventCallback =>
    (ev: SyntheticEvent): void => {
      const inputElement = ev.target as HTMLInputElement;
      const newTriggerInputs = {
        ...triggers,
        [field]: inputElement.type === 'number' ? inputElement.valueAsNumber : inputElement.value,
      };
      setTriggers(newTriggerInputs);
    };

  /**
   * onchange handler for checkbox inputs in this dialog.
   * This returns a curried function that calls the setState function passed in.
   *
   * @param setter      The setState function.
   */
  const handleCheckboxChange =
    (setter: (value: boolean) => void) =>
    (ev: SyntheticEvent): void => {
      const inputElement = ev.target as HTMLInputElement;
      setter(inputElement.checked);
    };

  /**
   * Convert trigger inputs to a trigger expression.
   */
  const transformTriggerInputs = (): string => {
    const op = triggers.type === 'less' || triggers.type === 'fewer' ? '<' : '>';
    let facetFunction = 'count';
    switch (triggerType) {
      case TriggerType.TABLE:
        facetFunction = triggers.function || '';
        break;
      case TriggerType.FREQUENCY:
        facetFunction = 'count';
        break;
      case TriggerType.AVERAGE:
        facetFunction = 'countPerSecond';
        break;
    }

    const minutes = triggers.minutes;
    const threshold = triggers.value;
    const filter = alertInfo.trigger;
    return `${facetFunction}:${minutes} minutes(${filter}) ${op} ${threshold}`;
  };

  const prepareAlertToSave = (): AlertInput => {
    const alertToSave = { ...alertInfo };

    if (!useGracePeriod) {
      alertToSave.gracePeriod = 0;
    }
    if (!useRenotifyPeriod) {
      alertToSave.renotifyPeriod = 0;
    }
    if (selectedResolutionOption === RESOLUTION_DISABLED) {
      alertToSave.resolutionDelay = 9999;
    }
    if (selectedResolutionOption === RESOLUTION_IMMEDIATE_ENABLED) {
      alertToSave.resolutionDelay = 0;
    }
    if (triggerType) {
      alertToSave.trigger = transformTriggerInputs();
    }

    return alertToSave;
  };

  const hasInvalidAlertInput = (): boolean => {
    if (!alertInfo.trigger) {
      setPageAlertConfig({
        message: 'Please enter a search string or other filter expression and try again.',
        autoHide: true,
      });
      return true;
    }

    return false;
  };

  // Create the trigger and save the alert
  const handleSaveAlert = (): void => {
    if (hasInvalidAlertInput()) {
      return;
    }

    const alertToSave = prepareAlertToSave();
    handleSubmit(alertToSave);
  };

  // Create the trigger and save the alert
  const handleSaveAsNewAlert = (): void => {
    if (hasInvalidAlertInput()) {
      return;
    }

    const alertToSave = prepareAlertToSave();
    const newAlert = pick(alertToSave, [
      'alertAddresses',
      'description',
      'gracePeriod',
      'renotifyPeriod',
      'resolutionDelay',
      'trigger',
    ]);

    if (handleSaveAsNewSubmit) {
      handleSaveAsNewSubmit(newAlert);
    }
  };

  const inputItems: InputItemsType = [
    {
      label: 'Description',
      as: 'textarea',
      placeholder: 'Description (optional)',
      value: alertInfo.description,
      onChangeHandler: handleInputChange('description'),
      testId: ELEMENT.saveAlertDialogDescription,
    },
    {
      label: 'Email / Webhook',
      as: 'input',
      value: alertInfo.alertAddresses,
      onChangeHandler: handleInputChange('alertAddresses'),
      placeholder: defaultAlertPlaceholder,
      testId: ELEMENT.saveAlertDialogEmail,
    },
    {
      label: triggerType ? 'Filter' : 'Trigger',
      as: 'textarea',
      value: alertInfo.trigger,
      onChangeHandler: handleInputChange('trigger'),
      testId: ELEMENT.saveAlertDialogFilter,
    },
  ];

  const inputSection = inputItems.map((item, index) => {
    const controlId = `SaveAlertDialog__${index}`;
    return (
      <BootstrapForm.Group controlId={controlId} as={BootstrapForm.Row} key={controlId}>
        <BootstrapForm.Label className="SaveAlertDialog__Label" column sm={3}>
          {item.label}
        </BootstrapForm.Label>
        <BootstrapCol sm={9}>
          <BootstrapForm.Control
            placeholder={item.placeholder}
            as={item.as}
            value={item.value}
            onChange={item.onChangeHandler}
            data-test-id={item.testId}
          />
        </BootstrapCol>
      </BootstrapForm.Group>
    );
  });

  // render another type of trigger inputs that allow the user to select trigger parameters
  if (triggerType) {
    const triggerContext = TRIGGER_FORM_INFO[triggerType];
    inputSection.push(
      <BootstrapForm.Group controlId="SaveAlertDialog__Trigger" as={BootstrapRow} key="SaveAlertDialog__Trigger">
        <BootstrapForm.Label className="SaveAlertDialog__Label" column sm={3}>
          Trigger
        </BootstrapForm.Label>
        <BootstrapCol sm={9}>
          <BootstrapRow className="SaveAlertDialog__CompositeRow">
            {triggerContext.frontText}
            {triggerType === TriggerType.TABLE && (
              <BootstrapCol sm={4}>
                <BootstrapForm.Control
                  as="select"
                  defaultValue={triggerContext.functions?.[0]}
                  onChange={handleTriggerChange('function')}
                >
                  {triggerContext?.functions?.map((item) => (
                    <option key={item} className="SaveAlertDialog__Option">
                      {item}
                    </option>
                  ))}
                </BootstrapForm.Control>
              </BootstrapCol>
            )}
            <BootstrapCol sm={3}>
              <BootstrapForm.Control
                as="select"
                defaultValue={triggerContext.measureWords[0]}
                onChange={handleTriggerChange('type')}
                data-test-id={ELEMENT.saveAlertDialogTrigger}
              >
                {triggerContext.measureWords.map((item, index) => (
                  <option
                    key={item}
                    className="SaveAlertDialog__Option"
                    data-test-id={createTestId(ELEMENT.saveAlertDialogTriggerItem, index)}
                  >
                    {item}
                  </option>
                ))}
              </BootstrapForm.Control>
            </BootstrapCol>
            than
            <BootstrapCol sm={2}>
              <BootstrapForm.Control
                as="input"
                defaultValue={triggers.value}
                type="number"
                onChange={handleTriggerChange('value')}
                data-test-id={ELEMENT.saveAlertDialogTriggerValue}
              />
            </BootstrapCol>
            {triggerContext.endText}
            <BootstrapCol sm={2}>
              <BootstrapForm.Control
                as="input"
                defaultValue={triggers.minutes}
                type="number"
                onChange={handleTriggerChange('minutes')}
                data-test-id={ELEMENT.saveAlertDialogTriggerDuration}
              />
            </BootstrapCol>
            minutes
          </BootstrapRow>
        </BootstrapCol>
      </BootstrapForm.Group>,
    );
  }

  const checkboxItems = [
    {
      label: 'Grace Period',
      checkboxLabelElement: (
        <>
          <CheckboxRadio
            type="checkbox"
            className="SaveAlertDialog__Checkbox"
            checked={useGracePeriod}
            onChange={handleCheckboxChange(setUseGracePeriod)}
            testId={ELEMENT.saveAlertDialogGracePeriodOn}
          />
          Only if it stays triggered for
          <BootstrapCol sm={3}>
            <BootstrapForm.Control
              as="input"
              type="number"
              defaultValue={alertInfo.gracePeriod}
              onChange={handleInputChange('gracePeriod')}
              data-test-id={ELEMENT.saveAlertDialogGracePeriod}
            />
          </BootstrapCol>
          {' minutes'}
        </>
      ),
    },
    {
      label: 'Reminder Period',
      checkboxLabelElement: (
        <>
          <CheckboxRadio
            type="checkbox"
            className="SaveAlertDialog__Checkbox"
            checked={useRenotifyPeriod}
            onChange={handleCheckboxChange(setUseRenotifyPeriod)}
            testId={ELEMENT.saveAlertDialogReminderOn}
          />
          Every
          <BootstrapCol sm={3}>
            <BootstrapForm.Control
              as="input"
              type="number"
              defaultValue={alertInfo.renotifyPeriod}
              onChange={handleInputChange('renotifyPeriod')}
              data-test-id={ELEMENT.saveAlertDialogReminderPeriod}
            />
          </BootstrapCol>
          {' minutes that it stays triggered'}
        </>
      ),
    },
  ];

  const checkboxSection = checkboxItems.map((item, index) => {
    const controlId = `SaveAlertDialog__${index}`;
    return (
      <BootstrapForm.Group controlId={controlId} as={BootstrapRow} key={controlId}>
        <BootstrapForm.Label className="SaveAlertDialog__Label" column sm={3}>
          {item.label}
        </BootstrapForm.Label>
        <BootstrapCol sm={9}>
          <BootstrapForm.Check inline type="checkbox">
            <BootstrapForm.Row className="SaveAlertDialog__CompositeRow">{item.checkboxLabelElement}</BootstrapForm.Row>
          </BootstrapForm.Check>
        </BootstrapCol>
      </BootstrapForm.Group>
    );
  });

  const handleResolutionSelection = (event: ChangeEvent<HTMLInputElement>): void => {
    setSelectedResolutionOption(event.target.value);
  };

  const resolutionDelaySection = (
    <BootstrapForm.Group as={BootstrapRow}>
      <BootstrapForm.Label as="legend" column sm={3} className="SaveAlertDialog__Label">
        Resolution
      </BootstrapForm.Label>
      <BootstrapCol sm={9}>
        <CheckboxRadio
          type="radio"
          label="When alert resolves, send notification immediately"
          name="formHorizontalRadios"
          id="formHorizontalRadios1"
          checked={selectedResolutionOption === RESOLUTION_IMMEDIATE_ENABLED}
          onChange={handleResolutionSelection}
          value={RESOLUTION_IMMEDIATE_ENABLED}
          testId={ELEMENT.saveAlertDialogNotifyResolvedNow}
        />
        <BootstrapForm.Check inline type="radio" name="formHorizontalRadios" id="formHorizontalRadios2">
          <CheckboxRadio
            type="radio"
            className="SaveAlertDialog__Checkbox"
            onChange={handleResolutionSelection}
            checked={selectedResolutionOption === RESOLUTION_DELAY_ENABLED}
            value={RESOLUTION_DELAY_ENABLED}
            testId={ELEMENT.saveAlertDialogNotifyResolvedPeriod}
          />
          Notify when alert has been resolved for
          <BootstrapCol sm={3}>
            <BootstrapForm.Control
              as="input"
              type="number"
              defaultValue={alertInfo.resolutionDelay}
              onChange={handleInputChange('resolutionDelay')}
              data-test-id={ELEMENT.saveAlertDialogNotifyResolvedMinutes}
            />
          </BootstrapCol>
          minutes
        </BootstrapForm.Check>

        <CheckboxRadio
          type="radio"
          label="Do not send notification when alert resolves"
          name="formHorizontalRadios"
          id="formHorizontalRadios3"
          value={RESOLUTION_DISABLED}
          checked={selectedResolutionOption === RESOLUTION_DISABLED}
          onChange={handleResolutionSelection}
          testId={ELEMENT.saveAlertDialogNotifyResolvedNever}
        />
      </BootstrapCol>
    </BootstrapForm.Group>
  );

  const getAlertTemplateInfoMessage = (): Array<JSX.Element> => {
    const { numAlertsInGroup } = alert as AlertInfo;
    const isAlertTemplate = numAlertsInGroup && numAlertsInGroup > K.DEFAULT_NUM_ALERTS_IN_GROUP;

    return isAlertTemplate
      ? [<span>{`Any changes will be applied to all ${numAlertsInGroup} alerts that use this template.`}</span>]
      : [];
  };

  const footer = {
    leftItems: [...getAlertTemplateInfoMessage()],
    rightItems: [
      <Button variant={ButtonVariant.PRIMARY} onClick={handleSaveAlert} testId={ELEMENT.saveAlertDialogSave}>
        Save
      </Button>,
      handleSaveAsNewSubmit ? (
        <Button
          variant={ButtonVariant.SECONDARY}
          onClick={handleSaveAsNewAlert}
          testId={ELEMENT.saveAlertDialogSaveAsNew}
        >
          Save as New
        </Button>
      ) : null,
      <Button variant={ButtonVariant.SECONDARY} onClick={close} testId={ELEMENT.saveAlertDialogCancel}>
        Cancel
      </Button>,
    ],
  };

  // insert 'Save as new' button to the middle
  if (showSaveAsNew) {
    footer.rightItems.splice(
      1,
      0,
      <Button variant={ButtonVariant.SECONDARY} onClick={handleSaveAlert}>
        Save as new
      </Button>,
    );
  }

  return (
    <Dialog title={title} show={show} close={close} footer={footer} testId={ELEMENT.saveAlertDialog}>
      <BootstrapForm>
        {inputSection}
        {checkboxSection}
        {resolutionDelaySection}
      </BootstrapForm>
    </Dialog>
  );
};

export default SaveAlertDialog;
